import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowDown,
  ArrowRight,
  Monitor,
  Mic,
  Coffee,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Calendar,
  Clock,
  GraduationCap,
  Rocket
} from 'lucide-react';

// WhatsApp Icon SVG Component
const WhatsAppIcon = ({ className = "" }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Navigation Component
const Navigation = () => (
  <nav className="sticky top-0 z-50 bg-cream border-b-4 border-text-dark">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center py-4">
        <Link to="/" className="flex items-center">
          <span className="font-poppins font-black text-2xl md:text-3xl tracking-tight text-text-dark">
            BOOST<span className="text-pink">CHANGE</span>
          </span>
        </Link>
        <div className="flex items-center gap-4 md:gap-6">
          <Link to="/impact-boost-academy" className="hidden md:flex items-center gap-2 font-poppins font-semibold text-text-dark hover:text-pink transition-colors">
            <ArrowLeft className="w-4 h-4" /> Academy
          </Link>
          <button
            onClick={() => document.getElementById('formulario-registro')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-pink text-white font-poppins font-bold uppercase tracking-widest px-4 md:px-6 py-3 border-3 border-text-dark hover:translate-x-0.5 hover:translate-y-0.5 transition-all text-sm md:text-base"
            style={{ boxShadow: '4px 4px 0px #1A1A1A', borderRadius: '0px', border: '3px solid #1A1A1A' }}
          >
            Inscríbete
          </button>
        </div>
      </div>
    </div>
  </nav>
);

// SECTION 1 - HERO (UPDATED)
const HeroSection = () => {
  return (
    <section className="bg-cream py-12 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Left Column - 55% */}
          <div className="md:col-span-7 space-y-6">
            {/* Tag Pills - FIX #2: Course name prominent */}
            <div className="space-y-2">
              <div 
                className="inline-block bg-pink text-white px-4 py-2"
                style={{ border: '2px solid #1A1A1A', borderRadius: '0px' }}
              >
                <span className="font-poppins font-bold text-xs uppercase tracking-wide">
                  CURSO: MOVILIZACIÓN DE RECURSOS Y CRECIMIENTO SOSTENIBLE
                </span>
              </div>
              <p className="font-poppins text-sm text-gray-500">
                Impact Boost Academy · 3ra Edición
              </p>
            </div>

            {/* Title H1 - UPDATED */}
            <h1 className="font-poppins font-black text-4xl md:text-5xl lg:text-[52px] text-text-dark leading-[1.1]">
              Aprende a movilizar recursos y haz tu ONG{' '}
              <span className="text-pink">sostenible.</span>
            </h1>

            {/* Subtitle */}
            <p className="font-poppins text-base md:text-lg text-gray-700 leading-relaxed">
              Un programa de 5 semanas para dejar de depender de un solo financista — con herramientas prácticas, casos reales de América Latina y una comunidad de líderes del sector social.
            </p>

            {/* NEW: Course Details Card */}
            <div
              className="bg-white p-6"
              style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
            >
              <h3 className="font-poppins font-bold text-lg text-text-dark mb-3">
                Cómo está organizado el curso
              </h3>
              <p className="font-poppins text-sm text-gray-600 mb-4">
                Nuestro enfoque es 100% práctico. Sin teoría vacía; vienes a construir herramientas que usarás desde el día uno.
              </p>
              <div className="space-y-2">
                <p className="font-poppins text-sm text-text-dark">
                  <span className="mr-2">📅</span>
                  <strong>Cuándo:</strong> Inicia el 15 de abril de 2026 (5 sesiones en vivo).
                </p>
                <p className="font-poppins text-sm text-text-dark">
                  <span className="mr-2">🕡</span>
                  <strong>Horario:</strong> Miércoles de 18:30 a 20:30 (Hora Ecuador).
                </p>
                <p className="font-poppins text-sm text-text-dark">
                  <span className="mr-2">☕</span>
                  <strong>Dinámica:</strong> Clases interactivas + Coffee Hours semanales.
                </p>
                <p className="font-poppins text-sm text-text-dark">
                  <span className="mr-2">🎓</span>
                  <strong>Cierre:</strong> Certificado de participación incluido.
                </p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <button
                onClick={() => document.getElementById('formulario-registro')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-pink text-white font-poppins font-bold uppercase tracking-widest px-6 py-4 hover:translate-x-0.5 hover:translate-y-0.5 transition-all"
                style={{ border: '3px solid #1A1A1A', boxShadow: '4px 4px 0px #1A1A1A', borderRadius: '0px' }}
              >
                RESERVA TU CUPO
              </button>
            </div>
          </div>

          {/* Right Column - 45% */}
          <div className="md:col-span-5 relative">
            <div 
              className="w-full overflow-hidden bg-white"
              style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_project-status-62/artifacts/jlyftkr2_WhatsApp%20Image%202026-01-29%20at%204.39.03%20PM.jpeg"
                alt="Alumni del curso Movilización de Recursos"
                className="w-full h-auto"
                style={{ display: 'block' }}
              />
            </div>

            {/* Floating Badge 1 - Top Right */}
            <div
              className="absolute -top-3 -right-3 bg-pink text-white px-3 py-2"
              style={{ border: '3px solid #1A1A1A', boxShadow: '4px 4px 0px #1A1A1A', borderRadius: '0px' }}
            >
              <span className="font-poppins font-black text-sm">NPS 9.7/10</span>
            </div>

            {/* Floating Badge 2 - Bottom Left */}
            <div
              className="absolute -bottom-3 -left-3 bg-lime text-text-dark px-3 py-2"
              style={{ border: '3px solid #1A1A1A', boxShadow: '4px 4px 0px #1A1A1A', borderRadius: '0px' }}
            >
              <span className="font-poppins font-bold text-sm">+700 alumni formados</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// SECTION 2 - CÓMO FUNCIONA (UPDATED with equal height)
const HowItWorksSection = () => {
  const cards = [
    {
      icon: <Monitor className="w-8 h-8 text-white" />,
      iconBg: "bg-cyan",
      title: "Plataforma virtual 24/7",
      description: "Accede a materiales, grabaciones y entregables desde nuestra plataforma educativa — disponible en cualquier dispositivo durante el programa y 30 días después del cierre."
    },
    {
      icon: <Mic className="w-8 h-8 text-white" />,
      iconBg: "bg-pink",
      title: "5 sesiones en vivo",
      description: "Cada miércoles de 18:30 a 20:30 (hora Ecuador) por Zoom. Clases participativas con ejercicios aplicados a tu organización y análisis de casos reales de América Latina."
    },
    {
      icon: <Coffee className="w-8 h-8 text-text-dark" />,
      iconBg: "bg-lime",
      title: "Coffee Hours semanales",
      description: "Cada viernes de las semanas del programa tienes acceso directo al facilitador para resolver dudas en tiempo real, profundizar temas y conectar con otros líderes del sector."
    }
  ];

  return (
    <section className="bg-yellow py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark text-center mb-12 uppercase">
          ¿Cómo funciona el curso?
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          {cards.map((card) => (
            <div
              key={card.title}
              className="bg-white p-6 flex flex-col h-full"
              style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
            >
              <div 
                className={`${card.iconBg} p-4 inline-block mb-4 self-start`}
                style={{ border: '3px solid #1A1A1A', borderRadius: '0px' }}
              >
                {card.icon}
              </div>
              <h3 className="font-poppins font-bold text-xl text-text-dark mb-3">{card.title}</h3>
              <p className="font-poppins text-sm text-gray-600 leading-relaxed flex-grow">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// SECTION 3 - PARA QUIÉN ES
const ForWhoSection = () => {
  const cards = [
    {
      number: "1",
      badgeBg: "bg-cyan",
      painPoint: "¿Dependes de uno o dos donantes para sobrevivir el año?",
      title: "Lideras una organización",
      description: "Eres director/a ejecutivo/a o coordinador/a y necesitas una estrategia de financiamiento clara, diversificada y alineada a tu misión."
    },
    {
      number: "2",
      badgeBg: "bg-pink",
      painPoint: "¿Pasas más tiempo buscando fondos que ejecutando tu misión?",
      title: "Gestionas recursos",
      description: "Eres parte del equipo de recaudación o proyectos y quieres herramientas concretas para mapear oportunidades y presentar propuestas con impacto."
    },
    {
      number: "3",
      badgeBg: "bg-lime",
      painPoint: "¿Tu modelo de financiamiento depende demasiado de la suerte?",
      title: "Consolidas tu modelo",
      description: "Tienes un emprendimiento social en crecimiento y buscas construir sostenibilidad financiera que dure más allá de un proyecto."
    }
  ];

  return (
    <section className="bg-cream py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark text-center mb-12 uppercase">
          Este curso es para ti si…
        </h2>

        <div className="grid md:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div
              key={card.title}
              className="bg-white p-6 pt-8 relative h-full flex flex-col"
              style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
            >
              {/* Number Badge - Positioned as tab */}
              <div
                className={`${card.badgeBg} absolute -top-4 -left-4 w-10 h-10 flex items-center justify-center`}
                style={{ border: '3px solid #1A1A1A', borderRadius: '0px' }}
              >
                <span className="font-poppins font-black text-xl text-text-dark">{card.number}</span>
              </div>

              <p className="font-poppins text-sm italic text-gray-500 mb-4">"{card.painPoint}"</p>
              <h3 className="font-poppins font-bold text-xl text-text-dark mb-3">{card.title}</h3>
              <p className="font-poppins text-sm text-gray-600 leading-relaxed flex-grow">{card.description}</p>
            </div>
          ))}
        </div>

        <p className="font-poppins text-sm italic text-gray-500 text-center mt-10">
          No necesitas experiencia previa en recaudación — solo claridad sobre tu misión y ganas de actuar.
        </p>
      </div>
    </section>
  );
};

// SECTION 4 - QUÉ VAS A LOGRAR (UPDATED with better badge integration)
const ObjectivesSection = () => {
  const objectives = [
    { badgeBg: "bg-cyan", title: "Diseñar tu estrategia de recaudación", description: "Construye una estrategia alineada a tu misión que conecte con los financistas correctos." },
    { badgeBg: "bg-pink", title: "Mapear y priorizar oportunidades", description: "Identifica fuentes de financiamiento locales, regionales y globales con criterios claros." },
    { badgeBg: "bg-lime", title: "Usar IA sin perder autenticidad", description: "Automatiza procesos de recaudación con herramientas prácticas y éticas." },
    { badgeBg: "bg-yellow", title: "Presentar presupuestos con impacto", description: "Construye presupuestos claros que generen confianza en tus financistas." },
    { badgeBg: "bg-purple", title: "Diseñar tu modelo de sostenibilidad", description: "Diversifica tus ingresos y construye una organización resiliente a largo plazo." },
  ];

  return (
    <section className="bg-white py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark text-center mb-12 uppercase">
          Al finalizar, serás capaz de:
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          {objectives.map((obj, i) => (
            <div
              key={obj.title}
              className="bg-cream p-6 relative h-full flex flex-col"
              style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
            >
              {/* Number Badge - Inside card at top */}
              <div
                className={`${obj.badgeBg} w-8 h-8 flex items-center justify-center mb-4`}
                style={{ border: '2px solid #1A1A1A', borderRadius: '0px' }}
              >
                <span className="font-poppins font-black text-sm text-text-dark">{i + 1}</span>
              </div>

              <h3 className="font-poppins font-bold text-lg text-text-dark mb-2">{obj.title}</h3>
              <p className="font-poppins text-sm text-gray-600 leading-relaxed flex-grow">{obj.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// SECTION 5 - LAS 5 SESIONES (UPDATED CONTENT)
const SessionsSection = () => {
  const [openSession, setOpenSession] = useState(0);

  const sessions = [
    {
      badgeBg: "bg-cyan",
      date: "15 de abril",
      title: "Estrategia de Recaudación: De la Misión a la Acción",
      intro: "Deja de pedir por pedir. La recaudación efectiva empieza con una claridad absoluta sobre tu impacto.",
      bullets: [
        "Aprende a articular tu misión de forma poderosa y persuasiva.",
        "Construye los cimientos comunicacionales de tu estrategia."
      ],
      deliverables: "Caja de mensajes lista para propuestas y redes, formato de clasificación de donantes y plantillas para cada canal."
    },
    {
      badgeBg: "bg-pink",
      date: "22 de abril",
      title: "Plan de Acción: Mapeo, Priorización y Oportunidades",
      intro: "Pasa de la teoría a la acción inmediata. Deja de perder tiempo en convocatorias que no encajan con tu ONG.",
      bullets: [
        "Mapea, filtra y prioriza a los donantes correctos.",
        "Identifica oportunidades reales y viables de financiamiento a corto plazo."
      ],
      deliverables: "Matriz de estrategia accionable, formato de priorización y listado de 6 oportunidades de financiamiento para tu trimestre."
    },
    {
      badgeBg: "bg-lime",
      date: "29 de abril",
      title: "IA para la Recaudación: Estrategias para Mayor Impacto",
      intro: "Multiplica el tiempo de tu equipo integrando tecnología de vanguardia de forma ética.",
      bullets: [
        "Investiga prospectos de donantes y automatiza procesos tediosos.",
        "Redacta propuestas ganadoras con IA sin perder tu voz auténtica."
      ],
      deliverables: "Guía de herramientas, flujo de trabajo asistido por IA y base de prompts para ChatGPT/Claude."
    },
    {
      badgeBg: "bg-yellow",
      date: "6 de mayo",
      title: "Presupuestos y Planificación Financiera",
      intro: "Los números cuentan la historia de tu impacto. Haz que jueguen a tu favor.",
      bullets: [
        "Construye presupuestos que generen confianza inmediata.",
        "Aprende a estructurar y defender tus costos operativos (overhead)."
      ],
      deliverables: "Plantilla de presupuesto anual personalizable y herramientas para presentar números con impacto."
    },
    {
      badgeBg: "bg-purple",
      date: "13 de mayo",
      title: "ONGs Sostenibles: Resiliencia a Largo Plazo",
      intro: "Rompe la dependencia de un solo donante y asegura el futuro de tu misión.",
      bullets: [
        "Diseña un modelo basado en casos de éxito en América Latina.",
        "Diversifica tus fuentes de ingreso estratégicamente."
      ],
      deliverables: "Plan Estratégico de Sostenibilidad Organizacional, recursos de casos de éxito y acceso a red Alumni."
    }
  ];

  const getCheckColor = (bg) => {
    switch(bg) {
      case 'bg-cyan': return '#1ECAD3';
      case 'bg-pink': return '#F44C7F';
      case 'bg-lime': return '#93D500';
      case 'bg-yellow': return '#FFB71B';
      case 'bg-purple': return '#B14FC5';
      default: return '#1ECAD3';
    }
  };

  return (
    <section className="bg-cream py-16 md:py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark text-center mb-12 uppercase">
          El programa sesión a sesión
        </h2>

        <div className="space-y-4">
          {sessions.map((session, i) => (
            <div
              key={session.title}
              className="bg-white overflow-hidden"
              style={{ border: '3px solid #1A1A1A', borderRadius: '0px' }}
            >
              {/* Header */}
              <button
                onClick={() => setOpenSession(openSession === i ? -1 : i)}
                className="w-full p-4 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors"
              >
                <div
                  className={`${session.badgeBg} px-3 py-1 flex-shrink-0`}
                  style={{ border: '2px solid #1A1A1A', borderRadius: '0px' }}
                >
                  <span className="font-poppins font-bold text-xs text-text-dark uppercase">
                    Sesión {i + 1}
                  </span>
                </div>
                <span className="font-poppins font-bold text-sm text-gray-500">{session.date}</span>
                <span className="font-poppins font-bold text-base text-text-dark flex-1">{session.title}</span>
                {openSession === i ? (
                  <ChevronUp className="w-5 h-5 text-text-dark flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-dark flex-shrink-0" />
                )}
              </button>

              {/* Content - FIX #3: Better readability */}
              {openSession === i && (
                <div className="p-5 pt-0 border-t-2 border-gray-200">
                  <p className="font-poppins text-base text-gray-800 mb-4 leading-relaxed font-medium">
                    {session.intro}
                  </p>
                  <ul className="space-y-3 mb-5">
                    {session.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-3">
                        <span className="text-pink font-bold mt-0.5">—</span>
                        <span className="font-poppins text-base text-gray-700 leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-start gap-3 bg-gray-100 p-4" style={{ borderRadius: '0px' }}>
                    <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: getCheckColor(session.badgeBg) }} />
                    <span className="font-poppins text-base text-gray-800 leading-relaxed">
                      <strong className="font-black text-text-dark">Entregables:</strong> {session.deliverables}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// SECTION 6 - TESTIMONIOS (UPDATED - 2 columns)
const TestimonialsSection = () => {
  return (
    <section className="bg-text-dark py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-white text-center mb-12 uppercase">
          Lo que dicen nuestros alumni
        </h2>

        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Left Column - Text */}
          <div className="space-y-6">
            <h3 className="font-poppins font-bold text-2xl text-white">
              El impacto de nuestra metodología
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="bg-cyan p-2 flex-shrink-0" style={{ border: '2px solid #1A1A1A', borderRadius: '0px' }}>
                  <span className="font-poppins font-black text-lg text-text-dark">+700</span>
                </div>
                <p className="font-poppins text-white/90">
                  profesionales del sector social formados desde 2025.
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="bg-lime p-2 flex-shrink-0" style={{ border: '2px solid #1A1A1A', borderRadius: '0px' }}>
                  <span className="font-poppins font-black text-lg text-text-dark">95%</span>
                </div>
                <p className="font-poppins text-white/90">
                  de nuestros alumni recomendaría este curso a sus colegas.
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="bg-pink p-2 flex-shrink-0" style={{ border: '2px solid #1A1A1A', borderRadius: '0px' }}>
                  <Rocket className="w-6 h-6 text-white" />
                </div>
                <p className="font-poppins text-white/90">
                  <strong>Aplica desde el día 1:</strong> Metodología enfocada en resultados, marcos prácticos y entregables reales para tu organización.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Video */}
          <div
            className="bg-white p-4"
            style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
          >
            {/* YouTube Shorts Embed */}
            <div className="mb-4">
              <iframe
                src="https://www.youtube.com/embed/oIymAVTucic"
                title="Testimonios Alumni"
                className="w-full"
                style={{ aspectRatio: '9/16', maxHeight: '450px', border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>

            {/* Names */}
            <p className="font-poppins font-bold text-base text-text-dark text-center mb-3">
              Jorge Luis Fernández <span className="text-cyan">|</span> Nata Arcos
            </p>

            {/* Badge */}
            <div className="flex justify-center">
              <div
                className="bg-cyan px-3 py-1"
                style={{ border: '2px solid #1A1A1A', borderRadius: '0px' }}
              >
                <span className="font-poppins font-bold text-xs uppercase text-text-dark">
                  Alumni · Movilización de Recursos
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// SECTION 7 - PRECIOS (UPDATED - Single unified card)
const PricingSection = () => {
  const benefits = [
    "5 sesiones en vivo y prácticas",
    "Acceso a plataforma y grabaciones",
    "Todos los entregables y plantillas",
    "Coffee Hours semanales",
    "Certificado verificable"
  ];

  return (
    <section className="bg-cream py-16 md:py-20">
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark text-center mb-12 uppercase">
          Invierte en tu organización
        </h2>

        {/* Single Unified Card */}
        <div
          className="bg-white p-8"
          style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
        >
          {/* Early Bird Badge */}
          <div
            className="bg-pink text-white px-4 py-2 mb-6 text-center"
            style={{ border: '3px solid #1A1A1A', borderRadius: '0px' }}
          >
            <span className="font-poppins font-bold text-sm uppercase">🔥 EARLY BIRD — VÁLIDO SOLO HASTA EL 10 DE ABRIL</span>
          </div>

          {/* Prices */}
          <div className="text-center mb-6">
            <p className="font-poppins text-lg text-gray-400 line-through mb-1">Precio regular: $211 USD</p>
            <p className="font-poppins font-black text-5xl md:text-6xl text-cyan mb-3">$147.20 USD</p>
            
            <div
              className="bg-lime inline-block px-4 py-2"
              style={{ border: '2px solid #1A1A1A', borderRadius: '0px' }}
            >
              <span className="font-poppins font-bold text-sm text-text-dark">Ahorras $63.80</span>
            </div>
          </div>

          {/* Benefits List - FIX #5: Better spacing */}
          <div className="space-y-4 mb-8">
            {benefits.map((benefit) => (
              <div key={benefit} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-cyan flex-shrink-0" style={{ color: '#1ECAD3' }} />
                <span className="font-poppins text-base text-text-dark">{benefit}</span>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <button
            onClick={() => document.getElementById('formulario-registro')?.scrollIntoView({ behavior: 'smooth' })}
            className="w-full bg-pink text-white font-poppins font-bold uppercase tracking-widest py-4 hover:translate-x-0.5 hover:translate-y-0.5 transition-all text-center"
            style={{ border: '3px solid #1A1A1A', boxShadow: '4px 4px 0px #1A1A1A', borderRadius: '0px' }}
          >
            QUIERO MI DESCUENTO Y ASEGURAR MI CUPO
          </button>
        </div>

        {/* Group Note */}
        <div className="text-center mt-8">
          <p className="font-poppins text-sm text-gray-600 mb-2">
            ¿Tu organización quiere inscribir a más de una persona?
          </p>
          <a
            href="https://wa.me/593982525226?text=Hola%20Boost%20Change,%20me%20interesa%20inscribir%20a%20mi%20equipo%20en%20el%20curso%20de%20Movilizaci%C3%B3n%20de%20Recursos.%20%C2%BFMe%20ayudan%20con%20informaci%C3%B3n%20sobre%20tarifas%20grupales?"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-poppins font-bold text-sm hover:underline"
            style={{ color: '#25D366' }}
          >
            <WhatsAppIcon className="w-4 h-4" />
            Escríbenos por WhatsApp para tarifas grupales
          </a>
          <p className="font-poppins text-xs text-gray-400 mt-4">
            Reembolso completo garantizado hasta el 8 de abril de 2026. Pasada esa fecha, puedes transferir tu cupo.
          </p>
        </div>
      </div>
    </section>
  );
};

// SECTION 8 - FORMULARIO (UPDATED - Card with button to external form)
const RegistrationSection = () => {
  return (
    <section id="formulario-registro" className="bg-cyan py-16 md:py-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark mb-4 uppercase">
          ¿Lista/o para transformar tu organización?
        </h2>

        <p className="font-poppins text-lg text-text-dark mb-8">
          Los cupos son limitados. Asegura el tuyo antes del 15 de abril.
        </p>

        {/* Registration Card */}
        <div
          className="bg-white p-8 text-center"
          style={{ border: '3px solid #1A1A1A', boxShadow: '8px 8px 0px #1A1A1A', borderRadius: '0px' }}
        >
          <h3 className="font-poppins font-bold text-2xl text-text-dark mb-4">
            Asegura tu cupo hoy
          </h3>
          <p className="font-poppins text-sm text-gray-600 mb-6 leading-relaxed">
            Llena el formulario de pre-registro. Al finalizar, recibirás inmediatamente un correo con el enlace para realizar tu pago y confirmar tu espacio.
          </p>
          
          <a
            href="https://form.jotform.com/260825328442053"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-pink text-white font-poppins font-bold uppercase tracking-widest px-8 py-4 hover:translate-x-0.5 hover:translate-y-0.5 transition-all"
            style={{ border: '3px solid #1A1A1A', boxShadow: '4px 4px 0px #1A1A1A', borderRadius: '0px' }}
          >
            Llenar formulario de inscripción
            <Rocket className="w-5 h-5" />
          </a>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="font-poppins text-sm text-gray-500 mb-2">¿Prefieres hablar primero?</p>
            <a
              href="https://wa.me/593982525226"
              target="_blank"
              rel="noopener noreferrer"
              className="font-poppins font-bold text-text-dark hover:underline"
            >
              Escríbenos al +593 982525226
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// SECTION 9 - FAQ
const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(-1);

  const faqs = [
    {
      question: "¿Necesito experiencia previa en recaudación?",
      answer: "No. El curso está diseñado para todos los niveles. Lo importante es tener claridad básica sobre la misión de tu organización."
    },
    {
      question: "¿Cómo funciona la plataforma virtual?",
      answer: "Al confirmar tu inscripción recibirás acceso a nuestra plataforma educativa con todos los materiales, entregables y grabaciones. Disponible 24/7 en cualquier dispositivo durante el programa y 30 días después del cierre."
    },
    {
      question: "¿Las sesiones quedan grabadas?",
      answer: "Sí. Todas las sesiones quedan grabadas y disponibles en la plataforma para que las revises cuando lo necesites."
    },
    {
      question: "¿Qué son los Coffee Hours?",
      answer: "Espacios semanales los viernes de las semanas del programa — abiertos e incluidos en tu inscripción. Resuelves dudas en vivo, profundizas los temas de la semana y conectas con otros líderes del sector social."
    },
    {
      question: "¿Puedo inscribir a más de una persona de mi equipo?",
      answer: "¡Claro! Escríbenos a info@boost-change.com o al WhatsApp +593 982525226 para conocer nuestras tarifas grupales."
    },
    {
      question: "¿Cuál es la política de reembolso?",
      answer: "Realizamos reembolsos completos hasta 7 días antes del inicio (hasta el 8 de abril de 2026). Pasada esa fecha no hay reembolso, pero puedes transferir tu cupo a otra persona."
    },
    {
      question: "¿Recibo un certificado al finalizar?",
      answer: "Sí. Participantes que completen al menos 4 de las 5 sesiones reciben certificado digital emitido por Impact Boost Academy — Boost Change."
    }
  ];

  return (
    <section className="bg-white py-16 md:py-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark text-center mb-12 uppercase">
          Preguntas frecuentes
        </h2>

        <div className="space-y-0">
          {faqs.map((faq, i) => (
            <div
              key={faq.question}
              className="border-b-2 border-text-dark"
            >
              <button
                onClick={() => setOpenFAQ(openFAQ === i ? -1 : i)}
                className="w-full py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
              >
                <span className="font-poppins font-bold text-base text-text-dark pr-4">{faq.question}</span>
                {openFAQ === i ? (
                  <ChevronUp className="w-5 h-5 text-text-dark flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-dark flex-shrink-0" />
                )}
              </button>

              {openFAQ === i && (
                <div className="pb-5">
                  <p className="font-poppins text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-text-dark py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <Link to="/" className="font-poppins font-black text-2xl text-white">
          BOOST<span className="text-pink">CHANGE</span>
        </Link>
        <div className="flex gap-6">
          <Link to="/" className="font-poppins text-white/80 hover:text-pink transition-colors">Inicio</Link>
          <Link to="/impact-boost-academy" className="font-poppins text-white/80 hover:text-pink transition-colors">Academy</Link>
          <Link to="/sistemas-de-impacto" className="font-poppins text-white/80 hover:text-pink transition-colors">Sistemas</Link>
          <Link to="/boost-ops" className="font-poppins text-white/80 hover:text-pink transition-colors">Boost Ops</Link>
        </div>
        <p className="font-poppins text-white/60 text-sm">2026 Boost Change</p>
      </div>
    </div>
  </footer>
);

// Main Page Component
const MovilizacionRecursosPage = () => {
  // FIX #1: Force scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-cream">
      <Navigation />
      <main>
        <HeroSection />
        <HowItWorksSection />
        <ForWhoSection />
        <ObjectivesSection />
        <SessionsSection />
        <TestimonialsSection />
        <PricingSection />
        <RegistrationSection />
        <FAQSection />
      </main>
      <Footer />
    </div>
  );
};

export default MovilizacionRecursosPage;
